import { get, put } from './base';
import { RemoteData, done, failed, Done, Failed } from '../../utils/remote-data';
import { Organization, ScoreComponent, ScoreComponentsResponse } from '../../interfaces/organization';
import { Organization as OrganizationGQL } from '../../graphql/codegen/schemas';

export const getOrganizationById = async (organizationId: string): Promise<RemoteData<Organization>> => {
  try {
    const res = await get(`/organizations/${organizationId}`);
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};

export const updateBACInventory = async (organizationId: string, increase: boolean): Promise<RemoteData<any>> => {
  try {
    const res = await put(
      `/organizations/${organizationId}/indistinguishable-devices?deviceModelType=BAC&increase=${increase}`,
    );
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};

export const updateOrganization = async (
  organizationId: string,
  organizationUpdated: OrganizationGQL,
): Promise<Done<any> | Failed> => {
  try {
    const res = await put(`/organizations/${organizationId}`, organizationUpdated);
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};

export const getOrganizationEmailAlert = async (
  organizationId: string,
  alertCategory: string,
): Promise<Done<any> | Failed> => {
  try {
    const res = await get(`/organizations/${organizationId}/email-alert/${alertCategory}`);
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};

export const updateOrganizationEmailAlert = async (
  organizationId: string,
  alertCategory: string,
  settings: any,
): Promise<Done<any> | Failed> => {
  try {
    const res = await put(`/organizations/${organizationId}/email-alert/${alertCategory}`, settings);
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};

// /organizations/{{ account_id }}/email-alert/{{ alertCategory }}

export const updateOrganizationScoreComponents = async (
  organizationId: string,
  components: ReadonlyArray<ScoreComponent>,
): Promise<Done<unknown> | Failed> => {
  try {
    console.log('Updating score components:', components);

    // Basic client-side validation to avoid 400s
    const validNames = new Set(['engagement', 'stress', 'sleep', 'exercise', 'activity_count', 'mood', 'adherence']);

    if (!Array.isArray(components) || components.length === 0) {
      return failed({ message: 'Components must be a non-empty array' });
    }

    const invalidName = components.find(c => !validNames.has(c?.name));
    if (invalidName) {
      return failed({ message: `Invalid component name: ${invalidName?.name}` });
    }

    const hasZeroOrMissing = components.find(c => c?.weight == null || Number(c.weight) === 0);
    if (hasZeroOrMissing) {
      return failed({ message: 'Each component must have a weight > 0' });
    }

    const total = components.reduce((sum: number, c) => sum + Number(c.weight), 0);
    if (total !== 100) {
      return failed({ message: `Weights must sum to 100 (current total: ${total})` });
    }

    // API expects body shape: { components: [...] }
    const res = await put(`/score-component/organizations/${organizationId}`, { components });
    return done(res.data);
  } catch (err) {
    console.error('Error updating score components:', err);
    return failed(err);
  }
};

export const getOrganizationScoreComponents = async (
  organizationId: string,
): Promise<Done<ScoreComponentsResponse> | Failed> => {
  try {
    const res = await get(`/score-component/organizations/${organizationId}`);
    return done(res.data);
  } catch (err) {
    return failed(err);
  }
};
